import { observer } from "mobx-react-lite";
import { useRef } from "react";

import { useFormFieldId } from "../form/form-field-id-context";
import { BaseCombobox, type BaseComboboxOption } from "./base-combobox";
import { FieldProps, useField, useModelBinding } from "./utils";
import { ValidationTooltip } from "./validation-tooltip";

interface Props<V, M extends object = Record<string, any>> extends FieldProps<
  M,
  V
> {
  options?: BaseComboboxOption<V>[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

export const Combobox = observer(function Combobox<
  V,
  M extends object = Record<string, any>,
>({
  options = [],
  placeholder,
  disabled,
  required,
  ...restProps
}: Props<V, M>) {
  const formFieldId = useFormFieldId();
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  const { value, onChange } = useModelBinding(restProps);
  const { isInvalid, tooltip } = useField(hiddenInputRef, value);

  // Convert value to string for the hidden input
  const stringValue = value != null ? String(value) : "";

  return (
    <ValidationTooltip model={tooltip}>
      <input
        ref={hiddenInputRef}
        type="text"
        value={stringValue}
        onChange={() => {}}
        required={required}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
      <BaseCombobox
        id={formFieldId}
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={isInvalid}
      />
    </ValidationTooltip>
  );
});

export type { BaseComboboxOption as ComboboxOption };
